// Item analysis + MPS, computed on demand from a quiz's results + answer key
// (never stored). Aligned with the TESTIFY analytics reference: difficulty,
// discrimination (upper/lower 27%), point-biserial, distractor counts and their
// per-option percentages / upper-lower split / non-functioning + attractive
// flags, plus per-quiz KR-20 reliability, MPS and its 4-band mastery level,
// score dispersion (SD, SD as a share of the maximum) and the DepEd tier-binned
// score distribution.
//
// NULL-safe: discrimination, point-biserial, KR-20 and the DERIVED distractor
// fields return null when the math would be meaningless (see guardrails below).
// SD and both distributions are descriptive, defined at any n, and deliberately
// NOT subject to that guardrail — suppressing a valid descriptive statistic
// would be its own kind of dishonesty.
import type { AnswerKeyRecord, ResultRecord } from '../db/schema.js';
import { keyItems, correctOptionFor, isCorrectChoice } from '../scoring/score.js';
import { DEPED_BANDS, bandFor, type InterventionTier } from './depedTiers.js';

export type ItemFlag = 'too_difficult' | 'too_easy' | 'negative_d' | 'poor_d';

/**
 * A distractor chosen by fewer than this share of examinees is doing no work —
 * it is not a plausible wrong answer, so the item is effectively a 3-option item.
 * This 5% convention comes from the measurement literature (Haladyna & Downing),
 * NOT from DepEd; it is stated as such wherever the flag is shown.
 */
export const NON_FUNCTIONING_DISTRACTOR_RATE = 0.05;

export interface ItemStat {
  q: number;
  key: string;
  /** proportion who answered correctly (difficulty index p, higher = easier). */
  difficulty: number;
  /** upper-27% minus lower-27% correct rate (-1..1); null when not computable. */
  discrimination: number | null;
  /** raw correct counts in the upper/lower 27% groups (§1.6 cols 2-3); null when N/A. */
  upperCorrect: number | null;
  lowerCorrect: number | null;
  nUpper: number;
  nLower: number;
  /** point-biserial correlation of item score vs total score; null when N/A. */
  pointBiserial: number | null;
  /** count choosing each option, plus blanks/doubles. */
  distractors: Record<string, number>;
  /**
   * Each option's count as a whole-number percentage of n. null under the same
   * guardrail as discrimination — the counts above are always available, only the
   * derived reading of them is suppressed.
   */
  optionPct: Record<string, number> | null;
  /** count choosing each option within the upper / lower 27% group (Excel only). */
  upperByOption: Record<string, number> | null;
  lowerByOption: Record<string, number> | null;
  /** distractors chosen by fewer than NON_FUNCTIONING_DISTRACTOR_RATE of examinees. */
  nonFunctioning: string[] | null;
  /**
   * Distractors chosen by MORE upper-group than lower-group learners — the
   * concrete explanation for a negative discrimination index.
   */
  attractive: string[] | null;
  blank: number;
  double: number;
  correct: number;
  total: number; // responses to the item (= scored students)
  flag: ItemFlag | null;
}

/** One DepEd band of the tier-binned score distribution. */
export interface ScoreBand {
  tier: InterventionTier;
  /** "75–79" */
  range: string;
  /** DepEd descriptor; '' for the 80+ band, which the reference table leaves unnamed. */
  descriptor: string;
  count: number;
  /** share of the scored class, whole number. */
  pct: number;
}

export interface QuizAnalysis {
  n: number; // students included (scored)
  options: string[];
  meanScore: number;
  maxScore: number;
  totalScoreSum: number; // sum of raw scores (MPS numerator base)
  stdDev: number;
  /**
   * The same SD as a whole-number percentage of the maximum score. Raw SD is not
   * comparable across the nine presets — SD 4 is a wide spread on a 20-item quiz
   * and a tight one on a 100-item quiz — so this supplements the raw figure, it
   * does not replace it. Defined at any n: it is descriptive, not inferential.
   */
  sdPctOfMax: number;
  mps: number; // mean percentage score
  masteryLevel: string;
  kr20: number | null; // internal-consistency reliability (null when N/A)
  highest: number;
  lowest: number;
  /** true once the sample supports discrimination/point-biserial/KR-20. */
  statsAvailable: boolean;
  /** DepEd-recommended minimum for reliable item analysis (n >= 30). */
  reliableSample: boolean;
  items: ItemStat[];
  distribution: Array<{ score: number; count: number }>;
  /**
   * The distribution collapsed into the four DepEd bands by overall quiz
   * percentage, highest band first. The reference table specifies these tiers at
   * competency level; applying them to an overall quiz percentage is an extension
   * of it, and every rendering says so.
   */
  bandDistribution: ScoreBand[];
}

/** TESTIFY 4-band mastery classification for an MPS percentage. */
export function masteryDescriptor(pct: number): string {
  if (pct >= 75) return 'Mastered';
  if (pct >= 50) return 'Approaching Mastery';
  if (pct >= 25) return 'Developing';
  return 'Beginning';
}

export type Decision = 'Retain' | 'Revise' | 'Reject';

/** Difficulty-index level + decision (TESTIFY §1.6.1). Retain only mid-band. */
export function difficultyBand(p: number): { level: string; decision: Decision } {
  if (p <= 0.2) return { level: 'Very Difficult', decision: 'Revise' };
  if (p <= 0.4) return { level: 'Difficult', decision: 'Revise' };
  if (p <= 0.6) return { level: 'Average', decision: 'Retain' };
  if (p <= 0.8) return { level: 'Easy', decision: 'Revise' };
  return { level: 'Very Easy', decision: 'Revise' };
}

/** Discrimination-index level + decision (TESTIFY §1.6.2). null → not computable. */
export function discriminationBand(d: number | null): { level: string; decision: Decision | null } {
  if (d == null) return { level: '—', decision: null };
  if (d < 0) return { level: 'Negative', decision: 'Reject' };
  if (d <= 0.2) return { level: 'Poor', decision: 'Revise' };
  if (d <= 0.4) return { level: 'ND', decision: 'Revise' };
  if (d <= 0.6) return { level: 'MD', decision: 'Retain' };
  if (d <= 0.8) return { level: 'D', decision: 'Retain' };
  return { level: 'VD', decision: 'Retain' };
}

function pearson(xs: number[], ys: number[]): number | null {
  const n = xs.length;
  if (!n) return null;
  let sx = 0;
  let sy = 0;
  for (let i = 0; i < n; i++) {
    sx += xs[i];
    sy += ys[i];
  }
  const mx = sx / n;
  const my = sy / n;
  let cov = 0;
  let vx = 0;
  let vy = 0;
  for (let i = 0; i < n; i++) {
    const dx = xs[i] - mx;
    const dy = ys[i] - my;
    cov += dx * dy;
    vx += dx * dx;
    vy += dy * dy;
  }
  if (vx <= 0 || vy <= 0) return null; // no variance (e.g. all got the item right)
  return cov / Math.sqrt(vx * vy);
}

function chosenMap(r: ResultRecord): Map<number, { chosen: string | null; double: boolean; blank: boolean }> {
  return new Map(
    r.rawAnswers.map((a) => [
      a.q,
      { chosen: a.chosen, double: a.flags.includes('double'), blank: a.flags.includes('blank') || a.chosen === null },
    ]),
  );
}

function itemFlag(p: number, d: number | null): ItemFlag | null {
  if (d != null && d < 0) return 'negative_d';
  if (p < 0.2) return 'too_difficult';
  if (p > 0.9) return 'too_easy';
  if (d != null && d < 0.2) return 'poor_d';
  return null;
}

export function analyzeQuiz(results: ResultRecord[], key: AnswerKeyRecord, options: string[]): QuizAnalysis {
  const scored = results.filter((r) => r.score != null && r.maxScore != null);
  const n = scored.length;
  const qs = keyItems(key);
  const maxScore = scored[0]?.maxScore ?? qs.length;

  const totals = scored.map((r) => r.score ?? 0);
  const sortedTotals = [...totals].sort((a, b) => a - b);
  const meanScore = n ? totals.reduce((s, v) => s + v, 0) / n : 0;
  const variancePop = n ? totals.reduce((s, t) => s + (t - meanScore) ** 2, 0) / n : 0;
  const stdDev = Math.sqrt(variancePop);

  // Guardrails (TESTIFY §1.5): discrimination / point-biserial / KR-20 are only
  // meaningful with enough students AND non-zero score variance.
  const statsAvailable = n >= 10 && variancePop > 0;

  // upper / lower 27% groups by total score
  const ranked = [...scored].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
  const groupSize = Math.max(1, Math.round(n * 0.27));
  const upper = ranked.slice(0, groupSize);
  const lower = ranked.slice(-groupSize);
  const correctInGroup = (group: ResultRecord[], q: number) => {
    let c = 0;
    for (const r of group) {
      const m = chosenMap(r).get(q);
      if (m && !m.double && !m.blank && isCorrectChoice(key, q, m.chosen)) c++;
    }
    return c;
  };

  // per-student correct/incorrect (1/0) matrix, reused for point-biserial + KR-20
  const maps = scored.map(chosenMap);

  // The per-option upper/lower split reads the SAME groups as D above — a second,
  // differently-rounded split would let the two disagree on the same screen.
  const indexOf = new Map(scored.map((r, i) => [r, i]));
  const upperIdx = upper.map((r) => indexOf.get(r)!);
  const lowerIdx = lower.map((r) => indexOf.get(r)!);
  const optionCountsIn = (idx: number[], q: number): Record<string, number> => {
    const counts: Record<string, number> = Object.fromEntries(options.map((o) => [o, 0]));
    for (const i of idx) {
      const e = maps[i].get(q);
      // Blanks and double-marks are their own categories, never attributed to an
      // option — the same rule the per-item counts follow.
      if (e && !e.blank && !e.double && e.chosen && counts[e.chosen] !== undefined) counts[e.chosen]++;
    }
    return counts;
  };
  const isCorrect = (m: ReturnType<typeof chosenMap>, q: number) => {
    const e = m.get(q);
    return e && !e.double && !e.blank && isCorrectChoice(key, q, e.chosen) ? 1 : 0;
  };

  let sumPQ = 0;
  const items: ItemStat[] = qs.map((q) => {
    const keyOpt = correctOptionFor(key, q) ?? '';
    const distractors: Record<string, number> = Object.fromEntries(options.map((o) => [o, 0]));
    let blank = 0;
    let double = 0;
    let correct = 0;
    for (const m of maps) {
      const e = m.get(q);
      if (!e || e.blank) blank++;
      else if (e.double) double++;
      else {
        if (e.chosen && distractors[e.chosen] !== undefined) distractors[e.chosen]++;
        if (isCorrectChoice(key, q, e.chosen)) correct++;
      }
    }
    const difficulty = n ? correct / n : 0;
    const p = difficulty;
    sumPQ += p * (1 - p);

    const upperCorrect = statsAvailable ? correctInGroup(upper, q) : null;
    const lowerCorrect = statsAvailable ? correctInGroup(lower, q) : null;
    const discrimination =
      upperCorrect != null && lowerCorrect != null ? upperCorrect / upper.length - lowerCorrect / lower.length : null;
    const pointBiserial = statsAvailable ? pearson(maps.map((m) => isCorrect(m, q)), totals) : null;

    // Derived distractor readings share the discrimination guardrail: below 10
    // learners, or with no score variance, a "3% chose D" or an upper/lower
    // comparison is noise dressed as a finding. The raw counts above stay.
    const optionPct = statsAvailable
      ? (Object.fromEntries(options.map((o) => [o, Math.round((100 * (distractors[o] ?? 0)) / n)])) as Record<string, number>)
      : null;
    const upperByOption = statsAvailable ? optionCountsIn(upperIdx, q) : null;
    const lowerByOption = statsAvailable ? optionCountsIn(lowerIdx, q) : null;
    const isDistractor = (o: string) => o !== keyOpt;
    const nonFunctioning = statsAvailable
      ? options.filter((o) => isDistractor(o) && (distractors[o] ?? 0) / n < NON_FUNCTIONING_DISTRACTOR_RATE)
      : null;
    const attractive =
      upperByOption && lowerByOption
        ? options.filter((o) => isDistractor(o) && upperByOption[o] > lowerByOption[o])
        : null;

    return {
      q,
      key: keyOpt,
      difficulty,
      discrimination,
      upperCorrect,
      lowerCorrect,
      nUpper: upper.length,
      nLower: lower.length,
      pointBiserial,
      distractors,
      optionPct,
      upperByOption,
      lowerByOption,
      nonFunctioning,
      attractive,
      blank,
      double,
      correct,
      total: n,
      flag: itemFlag(difficulty, discrimination),
    };
  });

  const k = qs.length;
  const kr20 = statsAvailable && k > 1 ? (k / (k - 1)) * (1 - sumPQ / variancePop) : null;

  const distMap = new Map<number, number>();
  for (const t of sortedTotals) distMap.set(t, (distMap.get(t) ?? 0) + 1);
  const distribution = [...distMap.entries()].sort((a, b) => a[0] - b[0]).map(([score, count]) => ({ score, count }));

  // Tier-binned distribution. The learner's percentage is rounded BEFORE the band
  // comparison, so the number shown is the number the band decision used — 74.6%
  // reads as 75% and lands in `guided`, not `targeted`.
  const bandCounts = new Map<InterventionTier, number>(DEPED_BANDS.map((b) => [b.tier, 0]));
  for (const r of scored) {
    const max = r.maxScore || maxScore;
    const pct = max ? Math.round((100 * (r.score ?? 0)) / max) : 0;
    const band = bandFor(pct);
    bandCounts.set(band.tier, (bandCounts.get(band.tier) ?? 0) + 1);
  }
  const bandDistribution: ScoreBand[] = DEPED_BANDS.map((b) => {
    const count = bandCounts.get(b.tier) ?? 0;
    return { tier: b.tier, range: b.range, descriptor: b.descriptor, count, pct: n ? Math.round((100 * count) / n) : 0 };
  });

  const mps = maxScore ? Math.round((100 * meanScore) / maxScore) : 0;

  return {
    n,
    options,
    meanScore: round1(meanScore),
    maxScore,
    totalScoreSum: totals.reduce((s, v) => s + v, 0),
    stdDev: round2(stdDev),
    sdPctOfMax: maxScore ? Math.round((100 * stdDev) / maxScore) : 0,
    mps,
    masteryLevel: masteryDescriptor(mps),
    kr20: kr20 == null ? null : round2(kr20),
    highest: sortedTotals.length ? sortedTotals[sortedTotals.length - 1] : 0,
    lowest: sortedTotals.length ? sortedTotals[0] : 0,
    statsAvailable,
    reliableSample: n >= 30,
    items,
    distribution,
    bandDistribution,
  };
}

/** true if an item carries any distractor finding worth reading. */
export const hasDistractorFlag = (it: ItemStat): boolean =>
  (it.nonFunctioning?.length ?? 0) > 0 || (it.attractive?.length ?? 0) > 0;

/**
 * One item's distractor findings as readable phrases; null when the guardrail
 * suppressed them. Lives here so the screen, the A4 page and the spreadsheet all
 * word the same finding the same way.
 */
export function distractorFindings(it: ItemStat): string[] | null {
  if (it.nonFunctioning == null || it.attractive == null) return null;
  const out: string[] = [];
  if (it.nonFunctioning.length) out.push(`Non-functioning: ${it.nonFunctioning.join(', ')}`);
  if (it.attractive.length) out.push(`Attracts top group: ${it.attractive.join(', ')}`);
  return out;
}

const round1 = (n: number) => Math.round(n * 10) / 10;
const round2 = (n: number) => Math.round(n * 100) / 100;
